import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FieldStates } from 'sos-common/js/commonTypes/formField.types';
import { FileExtendedDetails } from 'sos-common/js/commonTypes/fileupload.types';
import { FormFieldFile } from 'sos-common/js/commonTypes/formField.types';
import { ValidationResult } from 'sos-common/js/commonTypes/validation.types';
import { filesMap } from 'sos-common/js/commonTypes/FormBaseStore.types';
import { ClientErrorsState } from 'sos-common/js/commonTypes/FormBaseStore.types';
import { StepValidationMap } from 'sos-common/js/commonTypes/validation.types';
import { CheckBox } from '../../..';
import { StepChildProps } from 'sos-common/js/components/WizardStepper/WizardStepper.types';

import {
  addFileActionFunc,
  addFilesAction,
  removeFileActionFunc,
  removeFilesAction,
  setFormValue,
  saveFormOrder,
  clearErrorDispatcher,
  clearError,
} from 'sos-eservice-common/js/Redux/Actions';

import { makeTranslator, translateFunc } from 'sos-common/js/utils/translations';
import { areAllFieldsErrorFree } from 'sos-common/js/utils/helpers';
import { OmbudOrderModel } from '../models/OmbudOrderModel';
import { OmbudFormStore } from '../models/OmbudStore';
import SummaryStep from '../../SummaryStep/SummaryStep';
import SummaryStepFragment from '../../SummaryStep/SummaryStepFragment';
import SendButton from 'sos-common/js/components/FormControls/SendButton';

export interface Step3Props extends StepChildProps {
  Translate?: translateFunc;
  files: filesMap;
  validationResults: ValidationResult[];
  orderId: string;
  postFile: addFileActionFunc;
  deleteFile: removeFileActionFunc;
  setFormValue?: typeof setFormValue;
  formModel: OmbudOrderModel;
  clearError: clearError;
  firstName: string;
  lastName: string;
  errors: ClientErrorsState;
  stepValidationMap: StepValidationMap;
  saveFormOrder?: typeof saveFormOrder;
  numberOfSentForToday: number;
  isSending: boolean;
  isSaving: boolean;
}

export interface Step3State {
  userHasCertifiedData: boolean;
}

class Step3 extends React.Component<Step3Props, Step3State> {
  constructor(props: any) {
    super(props);

    this.state = {
      userHasCertifiedData: false,
    };
  }

  public getTitle = () => 'Granska och skicka in';

  public addFiles = (field: FormFieldFile) => {
    this.props.clearError(field);
    this.props.postFile(field);
  };
  public removeFile = (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => {
    this.props.clearError(field);
    this.props.deleteFile(file, fieldName);
  };

  public onChangeField = (field: FieldStates) => {
    this.props.setFormValue(field);
  };

  onClickSave = (e: React.MouseEvent) => {
    e.preventDefault();
    this.props.saveFormOrder(this.props.navProps.lastStep);
  };

  validateFields = (...fieldNames: string[]): boolean => {
    const { validationResults } = this.props;
    return areAllFieldsErrorFree(validationResults, ...fieldNames);
  };

  getFormValue = (formName: string, detailedFormName = null): string => {
    let value: string = _.get(this.props.formModel, formName);
    if (value === null || value === undefined || value === '') {
      value = this.props.Translate('|OmbudsansokanForm|missingtask');
      if (detailedFormName !== null) {
        value = detailedFormName + ' saknas';
      }
    }
    value = String(value); //don't delete this line :p

    return value;
  };

  getPersonFields() {
    return ['Person.Fornamn', 'Person.Efternamn', 'Befattning', 'Telefon', 'Epost'];
  }

  getOrganizationFields() {
    return ['Organisation.Namn', 'Organisation.Organisationsnummer', 'Organisation.Epost'];
  }

  public componentDidMount() {
    this.props.reportToWizard({
      stepLabel: this.getTitle(),
    });
  }

  setUserHasCertified = (field: FieldStates) => {
    const hasCertified = field.value as boolean;
    this.setState({
      userHasCertifiedData: hasCertified,
    });

    this.props.setFormValue(field);
  };

  public render() {
    const {
      Translate,
      formModel,
      files,
      orderId,
      firstName,
      lastName,
      validationResults,
      numberOfSentForToday,
      isSaving = false,
    } = this.props;
    const noOrderId = !orderId;
    let sendDisabled = this.state.userHasCertifiedData && validationResults && validationResults.length === 0;

    return (
      <div id="step3" tabIndex={-1}>
        <h2>{Translate('|OmbudsansokanForm|summering')}</h2>
        <div className="form-summary">
          {/************/}
          {/*  STEP 1  */}
          {/************/}
          <SummaryStep
            stepId={0}
            goToStep={this.props.navProps.goToStep}
            isDone={_.get(this.props.stepValidationMap, '[0].isValid', false)}
            headerTitle={Translate('|OmbudsansokanForm|Oneterm')}
            headerBackString={Translate('|OmbudsansokanForm|gotoSteg1')}>
            {/*  Användarvillkor  */}
            <SummaryStepFragment isValid={this.validateFields('AccepteratVillkor')}>
              <div>
                <strong>{Translate('|OmbudsansokanForm|usercondition')}</strong>
                <br />
                {this.validateFields('AccepteratVillkor') && (
                  <span>{Translate('|OmbudsansokanForm|userconditionAccept')}</span>
                )}
                {!this.validateFields('AccepteratVillkor') && (
                  <span>{Translate('|OmbudsansokanForm|userconditionDecline')}</span>
                )}
              </div>
            </SummaryStepFragment>
          </SummaryStep>

          {/************/}
          {/*  STEP 2  */}
          {/************/}

          <SummaryStep
            stepId={1}
            goToStep={this.props.navProps.goToStep}
            isDone={_.get(this.props.stepValidationMap, '[1].isValid', false)}
            headerTitle={Translate('|OmbudsansokanForm|Twodelegete')}
            headerBackString={Translate('|OmbudsansokanForm|gotosteg2')}>
            <SummaryStepFragment isValid={this.validateFields(...this.getPersonFields())}>
              <div>
                <strong>{Translate('|OmbudsansokanForm|Twodelegete')}:</strong>
                <br />
                {this.getFormValue('Person.Fornamn')}
                <br />
                {this.getFormValue('Person.Efternamn')}
                <br />
                {this.getFormValue('Befattning', Translate('|OmbudsansokanForm|appointment'))}
                <br />
                {this.getFormValue('Telefon', Translate('|OmbudsansokanForm|telNr2'))}
                <br />
                {this.getFormValue('Epost', Translate('|OmbudsansokanForm|epost'))}
              </div>
            </SummaryStepFragment>

            <SummaryStepFragment isValid={this.validateFields(...this.getOrganizationFields())}>
              <div>
                <strong>{Translate('|OmbudsansokanForm|org2')}</strong>
                <br />
                {this.getFormValue('Organisation.Namn', Translate('|OmbudsansokanForm|organizationName'))}
                <br />
                {this.getFormValue('Organisation.Organisationsnummer', Translate('|OmbudsansokanForm|orgNr'))}
                <br />
                {this.getFormValue('Organisation.Epost', Translate('|OmbudsansokanForm|orgEpost'))}
                <br />
                {this.getFormValue(
                  'Organisation.Webbplats',
                  Translate('|OmbudsansokanForm|orgWebb') + ` (${Translate('|OmbudsansokanForm|mandatory2')})`
                )}
              </div>
            </SummaryStepFragment>

            <SummaryStepFragment isValid={this.validateFields('bilagor')}>
              <div>
                <strong>{Translate('|OmbudsansokanForm|attachfileSpec')}</strong>
                <br />
                {files['bilagor'].length === 0 && Translate('|OmbudsansokanForm|missingtask')}
                {files['bilagor'].map((file) => {
                  return (
                    <div
                      className={
                        'file-extension-icon form-summary__extension-icon ' +
                        file.OriginalFilename?.substring(file.OriginalFilename?.lastIndexOf('.') + 1)
                      }
                      key={`step2filenames_${file.FileId}`}>
                      {file.OriginalFilename}
                    </div>
                  );
                })}
              </div>
            </SummaryStepFragment>

            <SummaryStepFragment
              isValid={this.validateFields('fullmakt')}
              showNotObligatoryIcon={files['fullmakt'].length === 0}>
              <div>
                <strong>{Translate('|OmbudsansokanForm|warrantX')}</strong>
                <br />
                {files['fullmakt'].length === 0 && (
                  <span>
                    {Translate('|OmbudsansokanForm|missingtask') + ` (${Translate('|OmbudsansokanForm|mandatory2')})`}
                  </span>
                )}
                {files['fullmakt'].map((file) => {
                  return (
                    <div
                      className={
                        'file-extension-icon form-summary__extension-icon ' +
                        file.OriginalFilename?.substring(file.OriginalFilename?.lastIndexOf('.') + 1)
                      }
                      key={`step2filenames_${file.FileId}`}>
                      {file.OriginalFilename}
                    </div>
                  );
                })}
              </div>
            </SummaryStepFragment>
          </SummaryStep>
        </div>

        <hr className="formdivider formdivider--m-b-none" />
        <div className="terms-wrapper">
          <CheckBox
            fieldName="CheckboxIntygan"
            label={Translate('|OmbudsansokanForm|certification')}
            onChange={this.setUserHasCertified}
            value={!!_.get(formModel, 'CheckboxIntygan')}
          />
        </div>
        <hr className="formdivider formdivider--m-t-none formdivider--m-b-small" />

        {numberOfSentForToday >= 5 && (
          <div className="form-group-xs-small">
            <label>Du har uppnått max antal ansökningar man får göra per dygn.</label>
          </div>
        )}
        <SendButton
          hideTextOnSending={true}
          classNames="button button--blue-arrow button--ombud-submit"
          onClickSave={this.onClickSave}
          isSending={this.props.isSending}
          isSaving={isSaving}
          savingLabel="Sparar"
          sendDisabled={!sendDisabled || numberOfSentForToday >= 5}
        />
      </div>
    );
  }
}

const mapState2Props = (state: OmbudFormStore) => {
  const { errors, files, formModel, globals, validationState } = state;
  const { orderId, firstName, lastName, translations, numberOfSentForToday, isSaving } = globals;
  return {
    errors: errors,
    files: files,
    formModel: formModel,
    orderId: orderId,
    firstName: firstName,
    lastName: lastName,
    stepValidationMap: validationState.stepValidationMap,
    Translate: makeTranslator(translations),
    validationResults: validationState.validationResults,
    numberOfSentForToday: numberOfSentForToday,
    isSending: globals.isSending,
    isSaving: isSaving,
  } as Partial<Step3Props>;
};

const mapDispatch2Props = (dispatch) => {
  return {
    clearError: clearErrorDispatcher(dispatch),
    deleteFile: bindActionCreators(removeFilesAction, dispatch),
    postFile: bindActionCreators(addFilesAction, dispatch),
    setFormValue: bindActionCreators(setFormValue, dispatch),
    saveFormOrder: bindActionCreators(saveFormOrder, dispatch),
  };
};

const StepThree = connect(mapState2Props, mapDispatch2Props)(Step3);
export { StepThree };
